import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useProjectContext } from "../contexts/ProjectContext";
import { GanttChart } from "../components/Gantt/GanttChart";
import { GanttTaskForm } from "../components/Gantt/GanttTaskForm";
import {
  useGanttTasks,
  useCreateGanttTask,
  useUpdateGanttTask,
  useDeleteGanttTask,
} from "../hooks/useGantt";
import { useUpdateProject } from "../hooks/useProjects";
import { useTasks } from "../hooks/useTasks";
import { isArchivedGanttTask } from "../utils/planningStatus";
import {
  computeSchedule,
  computeDurataGiorni,
  summarizeSchedule,
} from "../utils/scheduling";
import type {
  GanttTask,
  CreateGanttTaskInput,
  UpdateGanttTaskInput,
} from "../types";

function formatDateIT(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("T")[0].split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function Gantt() {
  const { currentProject } = useProjectContext();
  const projectId = currentProject?.id ?? "";

  const {
    data: allGanttTasks = [],
    isLoading,
    error: fetchError,
  } = useGanttTasks(projectId);
  const fetchedTasks = useMemo(
    () => allGanttTasks.filter((task) => !isArchivedGanttTask(task)),
    [allGanttTasks],
  );
  const archivedTasks = useMemo(
    () => allGanttTasks.filter(isArchivedGanttTask),
    [allGanttTasks],
  );
  const { data: activities = [] } = useTasks(projectId);
  const pendingActivities = activities
    .filter(
      (task) =>
        !task.completata &&
        (task.categoria === "pianificazione" ||
          task.titolo.startsWith("SUBITO")),
    )
    .sort((a, b) => a.ordine - b.ordine);
  const appointments = activities.filter(
    (task) => !task.completata && task.scadenza,
  );
  const createTask = useCreateGanttTask();
  const updateTask = useUpdateGanttTask();
  const deleteTask = useDeleteGanttTask();
  const updateProject = useUpdateProject();

  const [error, setError] = useState<string | null>(null);

  // Planning controls (bound to project, persisted via PATCH)
  const [oreSettimana, setOreSettimana] = useState<number>(17.5);
  const [dataInizioCantiere, setDataInizioCantiere] = useState<string>("");

  // Local inline edits for oreStimate (keyed by task id) for live preview
  const [oreEdits, setOreEdits] = useState<Record<string, number>>({});

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<GanttTask | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Sync controls from project
  useEffect(() => {
    if (currentProject) {
      setOreSettimana(currentProject.oreSettimana ?? 17.5);
      setDataInizioCantiere(
        currentProject.dataInizioCantiere
          ? currentProject.dataInizioCantiere.split("T")[0]
          : "",
      );
    }
  }, [currentProject]);

  // Reset inline edits when the underlying tasks change
  useEffect(() => {
    setOreEdits({});
  }, [fetchedTasks]);

  const effectiveOre = useCallback(
    (task: GanttTask): number => oreEdits[task.id] ?? task.oreStimate ?? 0,
    [oreEdits],
  );

  // Tasks with live oreStimate overrides applied, for schedule preview
  const previewTasks = useMemo(
    () => fetchedTasks.map((t) => ({ ...t, oreStimate: effectiveOre(t) })),
    [fetchedTasks, effectiveOre],
  );

  // Computed schedule (live preview)
  const schedule = useMemo(
    () =>
      computeSchedule(previewTasks, {
        oreSettimana,
        dataInizioCantiere: dataInizioCantiere || null,
      }),
    [previewTasks, oreSettimana, dataInizioCantiere],
  );

  const summary = useMemo(() => summarizeSchedule(schedule), [schedule]);

  // Tasks to render in the bar chart: use computed (preview) dates so the chart
  // stays in sync even before an explicit "Ricalcola".
  const chartTasks: GanttTask[] = useMemo(
    () =>
      fetchedTasks.map((t) => {
        const s = schedule[t.id];
        return s
          ? {
              ...t,
              dataInizio: s.dataInizio,
              dataFine: s.dataFine,
              oreStimate: effectiveOre(t),
            }
          : t;
      }),
    [fetchedTasks, schedule, effectiveOre],
  );

  // --- Handlers ---

  const handleSaveOreSettimana = () => {
    if (!projectId) return;
    const value = Number(oreSettimana);
    if (!Number.isFinite(value) || value <= 0) return;
    if (value === currentProject?.oreSettimana) return;
    updateProject.mutate({ id: projectId, oreSettimana: value });
  };

  const handleSaveDataInizio = (value: string) => {
    setDataInizioCantiere(value);
    if (!projectId) return;
    updateProject.mutate({ id: projectId, dataInizioCantiere: value || null });
  };

  const handleOreStimateBlur = (task: GanttTask) => {
    const edited = oreEdits[task.id];
    if (edited === undefined || edited === task.oreStimate) return;
    updateTask.mutate({ id: task.id, projectId, data: { oreStimate: edited } });
  };

  const handleRecalculate = async () => {
    if (!projectId || fetchedTasks.length === 0) return;
    setIsRecalculating(true);
    setError(null);
    try {
      for (const task of fetchedTasks) {
        const s = schedule[task.id];
        if (!s) continue;
        const data: UpdateGanttTaskInput = {};
        const persistedStart = task.dataInizio.split("T")[0];
        const persistedEnd = task.dataFine.split("T")[0];
        if (s.dataInizio !== persistedStart) data.dataInizio = s.dataInizio;
        if (s.dataFine !== persistedEnd) data.dataFine = s.dataFine;
        const editedOre = oreEdits[task.id];
        if (editedOre !== undefined && editedOre !== task.oreStimate)
          data.oreStimate = editedOre;
        if (Object.keys(data).length > 0) {
          await updateTask.mutateAsync({ id: task.id, projectId, data });
        }
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Errore durante il ricalcolo",
      );
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleTaskClick = (task: GanttTask) => {
    setEditingTask(task);
    setShowForm(true);
  };

  const handleSubmit = async (
    data: CreateGanttTaskInput | UpdateGanttTaskInput,
  ) => {
    setError(null);
    try {
      if (editingTask) {
        await updateTask.mutateAsync({
          id: editingTask.id,
          projectId,
          data: data as UpdateGanttTaskInput,
        });
      } else {
        await createTask.mutateAsync({
          ...(data as CreateGanttTaskInput),
          projectId,
          ordine: allGanttTasks.length,
        });
      }
      setShowForm(false);
      setEditingTask(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Errore durante il salvataggio",
      );
    }
  };

  const handleDelete = async () => {
    if (!editingTask) return;
    setError(null);
    try {
      await deleteTask.mutateAsync({ id: editingTask.id, projectId });
      setShowForm(false);
      setEditingTask(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Errore durante l'eliminazione",
      );
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTask(null);
  };

  const isSaving =
    createTask.isPending || updateTask.isPending || deleteTask.isPending;

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">
          Seleziona un progetto per visualizzare il Gantt
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Cronoprogramma</h2>
          <p className="text-gray-600 mt-1">
            Sequenza dei lavori e date concordate, secondo disponibilità
            economica e dipendenze
          </p>
        </div>
      </div>

      {/* Planning controls */}
      {fetchedTasks.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ore a settimana
              </label>
              <input
                type="number"
                value={oreSettimana}
                min="1"
                step="0.5"
                onChange={(e) => setOreSettimana(Number(e.target.value))}
                onBlur={handleSaveOreSettimana}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Capacità del cantiere
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inizio cantiere
              </label>
              <input
                type="date"
                value={dataInizioCantiere}
                onChange={(e) => handleSaveDataInizio(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Durata totale</div>
                <div className="text-lg font-semibold text-gray-900">
                  {summary.giorniLavorativi} gg lav.
                </div>
                <div className="text-xs text-gray-500">
                  ≈ {summary.settimane} settiman
                  {summary.settimane === 1 ? "a" : "e"}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Fine cantiere</div>
                <div className="text-lg font-semibold text-gray-900">
                  {formatDateIT(summary.dataFine)}
                </div>
                <div className="text-xs text-gray-500">
                  Inizio: {formatDateIT(summary.dataInizio)}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Le date mostrate sono calcolate. Premi «Ricalcola» per salvarle
              sulle attività.
            </p>
            <button
              onClick={handleRecalculate}
              disabled={isRecalculating || fetchedTasks.length === 0}
              className="btn-primary disabled:opacity-50"
            >
              {isRecalculating ? "Ricalcolo..." : "Ricalcola pianificazione"}
            </button>
          </div>
        </div>
      )}
      {!currentProject?.immobile?.dataRogito && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-amber-800">
            Compila la data rogito nella Scheda Immobile per pianificare le
            attività
          </p>
          <Link
            to="/immobile"
            className="ml-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm whitespace-nowrap"
          >
            Vai alla Scheda Immobile
          </Link>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div></div>
        <button
          onClick={() => {
            setEditingTask(null);
            setShowForm(true);
          }}
          className="btn-primary"
        >
          + Nuova Attività
        </button>
      </div>

      {(error || fetchError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {error ||
              (fetchError instanceof Error
                ? fetchError.message
                : "Errore di caricamento")}
          </p>
          {error && (
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-500 hover:underline mt-1"
            >
              Chiudi
            </button>
          )}
        </div>
      )}

      {pendingActivities.length > 0 && (
        <section className="rounded-lg bg-white p-5 shadow space-y-3">
          <h3 className="font-semibold text-lg">Sequenza da pianificare</h3>
          <p className="text-sm text-gray-600">
            Le date dei lavori si fissano dopo progetto, preventivi e verifica
            della cassa. Le attività sotto non hanno ancora date di cantiere.
          </p>
          {appointments.length > 0 && (
            <ul className="space-y-1 text-sm">
              {appointments.map((task) => (
                <li key={task.id}>
                  <strong>{formatDateIT(task.scadenza)}</strong> — {task.titolo}
                </li>
              ))}
            </ul>
          )}
          <ol className="list-decimal pl-5 space-y-3">
            {pendingActivities.map((task) => (
              <li key={task.id}>
                <strong>{task.titolo}</strong>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {task.categoria === "pianificazione"
                    ? task.istruzioni?.replace("[RIALLINEATO 10/09/2026]\n", "")
                    : "Priorità immediata. Distinta della spesa e controlli nelle attività e nel CME."}
                </p>
              </li>
            ))}
          </ol>
          <Link
            to="/task"
            className="inline-block text-sm text-primary-700 underline"
          >
            Apri le attività per aggiornare esiti e avanzamento
          </Link>
        </section>
      )}
      {archivedTasks.length > 0 && (
        <details className="rounded-lg bg-gray-50 p-4">
          <summary className="cursor-pointer font-medium">
            Calendario precedente — {archivedTasks.length} fasi archiviate
          </summary>
          <p className="text-sm my-3">
            Date storiche, escluse dal ricalcolo e dalla pianificazione
            corrente.
          </p>
          <ul className="space-y-2 text-sm">
            {archivedTasks.map((task) => (
              <li key={task.id}>
                {task.nome.replace("[STORICO] ", "")}:{" "}
                {formatDateIT(task.dataInizio)} – {formatDateIT(task.dataFine)}
              </li>
            ))}
          </ul>
        </details>
      )}
      {/* Effort table: inline editable oreStimate + computed duration */}
      {fetchedTasks.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Fase</th>
                <th className="text-left px-4 py-2 font-medium">Ore stimate</th>
                <th className="text-left px-4 py-2 font-medium">Durata</th>
                <th className="text-left px-4 py-2 font-medium">
                  Inizio → Fine
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {fetchedTasks.map((task) => {
                const s = schedule[task.id];
                const durata = s
                  ? s.durataGiorni
                  : computeDurataGiorni(
                      {
                        oreStimate: effectiveOre(task),
                        durataFissaGiorni: task.durataFissaGiorni,
                      },
                      oreSettimana,
                    );
                const isFixed = task.durataFissaGiorni != null;
                return (
                  <tr key={task.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <button
                        onClick={() => handleTaskClick(task)}
                        className="font-medium text-gray-900 hover:text-primary-600 text-left"
                      >
                        {task.nome}
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      {isFixed ? (
                        <span className="text-gray-400 text-xs">
                          durata fissa
                        </span>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={effectiveOre(task)}
                          onChange={(e) =>
                            setOreEdits((prev) => ({
                              ...prev,
                              [task.id]: Math.max(0, Number(e.target.value)),
                            }))
                          }
                          onBlur={() => handleOreStimateBlur(task)}
                          className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {durata} gg lav.
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">
                      {s
                        ? `${formatDateIT(s.dataInizio)} → ${formatDateIT(s.dataFine)}`
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Gantt Chart */}
      <GanttChart tasks={chartTasks} onTaskClick={handleTaskClick} />

      {/* Task summary */}
      {fetchedTasks.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-gray-900">
              {fetchedTasks.length}
            </div>
            <div className="text-sm text-gray-500">Attività totali</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-green-600">
              {fetchedTasks.filter((t) => t.percentuale === 100).length}
            </div>
            <div className="text-sm text-gray-500">Completate</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-amber-600">
              {
                fetchedTasks.filter(
                  (t) => t.percentuale > 0 && t.percentuale < 100,
                ).length
              }
            </div>
            <div className="text-sm text-gray-500">In corso</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-gray-400">
              {fetchedTasks.filter((t) => t.percentuale === 0).length}
            </div>
            <div className="text-sm text-gray-500">Da iniziare</div>
          </div>
        </div>
      )}

      {/* Task Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingTask ? "Modifica Attività" : "Nuova Attività"}
            </h3>
            <GanttTaskForm
              task={editingTask}
              projectId={currentProject.id}
              tasks={fetchedTasks}
              onSubmit={handleSubmit}
              onDelete={editingTask ? handleDelete : undefined}
              onCancel={handleCancel}
              isLoading={isSaving}
            />
          </div>
        </div>
      )}
    </div>
  );
}
